// A to-do's row: the box, the title with its small marks, where it lives in lists that mix places (a real place only),
// and at one right edge, right-aligned, what has a date: how long it has waited, its later start, its deadline, a
// habit's streak (docs/65, A6; TodoRow.swift, MacTodoRow.swift). The stale list and the Archive have their own side.
// With a mouse a click selects and a double-click opens; on touch a tap opens (design decision 2).

import { useRef, useState, type KeyboardEvent, type MouseEvent } from "react";
import { destinationOf } from "../core/actions";
import { dayOf } from "../core/days";
import { laterStart, marksSomeday, waitingDays, type Route } from "../core/lists";
import type { Todo } from "../core/records";
import { t } from "../i18n";
import { useWindow } from "./context";
import { columnRowDropProps, rowDragProps, rowDropProps } from "./dnd";
import { deadlineLabel, deadlineSpoken, rowPlaceOf, splitEmoji, startLabel } from "./format";
import { Icon, Ring } from "./Icon";
import { Popover } from "./Menu";
import { MovePicker, WhenPicker } from "./Pickers";
import { closeOpenRow, useRowTouch, type SwipeState } from "./touch";
import { tickedInCard } from "./tickedInCard";
import { habitStatus } from "../core/habits";
import { badgeDetail, HabitBadgeView, restToday, scheduleLabel, useHabitOf, useHabitRowMenu } from "./Habits";

export function TodoRow({ todo, route, showsPlace, inToday, onComplete, onPlace, drop, bare = false, light = false }: {
  todo: Todo; route: Route; showsPlace: boolean; inToday: boolean; onComplete: (ids: readonly string[]) => void;
  /** The Day view: what is dropped on the row is planned before or after it. */
  onPlace?: (ids: readonly string[], where: "before" | "after") => void;
  /** Where a drop on the row goes, when not the list's own order (a column of the week). */
  drop?: ReturnType<typeof columnRowDropProps>;
  /** The title alone: no place, marks or dates (the week with Show Details off). */
  bare?: boolean;
  /**
   * Today with Show Details off (Setting.todayShowsDetails, Bastian 2026-11-04): the title with its marks, a deadline
   * that has come and a habit's streak; no place, tags, habit status or waiting.
   */
  light?: boolean;
}) {
  const window = useWindow();
  const { state, ui, layout, select, openTodo, model } = window;
  const { lists, today } = state;
  const selected = ui.selected.includes(todo.id);
  const done = todo.completedAt !== undefined;
  const due = todo.deadline !== undefined && todo.deadline <= today && !done;
  const later = laterStart(todo, today);
  const staleList = route.kind === "stale";
  // Just ticked, still in its place: a ring pulses from the box and the title is struck through (docs/59, 1a).
  const lingering = done && lists.isLingering(todo.id);
  // Ticked in its open card, where the moment played: the row shows it as it is, without a second ring.
  const quietTick = lingering && tickedInCard.has(todo.id);
  // Starts Someday, among others: the leaf after the title, and a grey title unless the stale look says more (3c).
  const someday = marksSomeday(todo, route);
  // A habit's day (docs/63): its badge at the row's end; with the row's other details, in their grey, the week's count
  // (kept once done) or "missed yesterday" in lilac (gone once done); on Habits' own page also how often (the display
  // decisions of 2026-10-29, point 2 C).
  const habit = useHabitOf(todo);
  const habitState = habit && !bare ? habitStatus(habit.badge, done) : undefined;
  const habitDetail = light ? undefined : badgeDetail(habitState);
  const often = habit && !bare && route.kind === "habits" ? scheduleLabel(habit.habit.schedule) : undefined;
  // A habit's day has no When: its swipe to the left is Not Today while that may be taken, else nothing (docs/63 15d);
  // a right-click opens its menu.
  const restable = habit !== null && route.kind !== "archive" && model.canRestHabitToday(habit.habit.id);
  const rowMenu = useHabitRowMenu(route.kind === "archive" ? undefined : habit?.habit, todo, onComplete);

  // Touch: sideways schedules (left) or moves (right); far left opens the row on Delete, which a tap does (docs/59,
  // 7a); a hold lifts the row to drag it.
  const element = useRef<HTMLDivElement>(null);
  const [swipe, setSwipe] = useState<SwipeState>({ action: null, armed: false, open: false });
  const [swiped, setSwiped] = useState<"when" | "move" | null>(null);
  const editing = ui.open !== null || ui.composing !== null;
  useRowTouch(element, {
    enabled: layout.touch && !editing && route.kind !== "archive",
    canDrag: !staleList,
    dragIDs: () => (ui.selected.includes(todo.id) ? ui.selected : [todo.id]),
    swipesLeft: habit === null || restable,
    onSwipeState: setSwipe,
    onSwipe: (action) => {
      if (action === "schedule" && habit) restToday(model, habit.habit.id, window.showToast);
      else setSwiped(action === "schedule" ? "when" : "move");
    },
  });
  const swipeButton = (event: MouseEvent, act: () => void) => {
    event.stopPropagation();
    closeOpenRow();
    act();
  };

  const click = (event: MouseEvent) => {
    if ((event.target as HTMLElement).closest("button")) return;
    if (ui.open !== null || ui.composing !== null) {
      document.dispatchEvent(new CustomEvent("todolix:close-card"));
      return;
    }
    if (layout.touch && !event.metaKey && !event.ctrlKey && !event.shiftKey) return openTodo(todo.id);
    select(todo.id, { toggle: event.metaKey || event.ctrlKey, range: event.shiftKey });
  };

  const key = (event: KeyboardEvent) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter") { event.preventDefault(); event.stopPropagation(); openTodo(todo.id); }
  };

  // Light keeps a birthday to-do's words ("Anna Berger turns 36"), not a real place (Bastian, 2026-11-04).
  const found = showsPlace && !bare ? rowPlaceOf(todo, lists) : null;
  const place = light && found?.kind !== "birthdays" ? null : found;
  // No note or reminder marks on rows: the card shows them (docs/65, A6).
  const marks: ("repeat" | "checklist" | "tag")[] = [];
  if (!bare) {
    if (todo.repeats) marks.push("repeat");
    if (todo.checklist.length && todo.habitID === undefined) marks.push("checklist");
    if (todo.tags.length && !light) marks.push("tag");
  }
  // In Today, from two days on: an hourglass, so "2d" does not read as a deadline (the UX review, 2026-10-24).
  const age = bare || light ? undefined : waitingDays(todo, route, today);

  let side = null;
  if (bare) {
    // The title alone.
  } else if (staleList) {
    const days = Math.max(1, Math.floor((Date.now() - (todo.keptAt ?? todo.createdAt).getTime()) / 86_400_000));
    side = (
      <span className="side stale-actions">
        <span className="stale-age" aria-label={t("Stale, added {count} days ago", { count: days })}><Icon name="stale" />{t("{count}d", { count: days })}</span>
        <button className="small-button" onClick={() => model.keep(todo.id)}>{t("Keep")}</button>
        <button className="small-button" onClick={() => model.keep(todo.id, { kind: "someday" })}>{t("Someday")}</button>
        <button className="small-button" onClick={() => { model.delete([todo.id]); window.showToast(t("To-do deleted"), true); }}>{t("Delete")}</button>
      </span>
    );
  } else if (route.kind === "archive" && todo.completedAt) {
    side = <span className="side">{dayOf(todo.completedAt, state.zone) === today ? t("Today") : ""}</span>;
  } else {
    const showsLater = later !== undefined && route.kind !== "scheduled";
    // Light: only a deadline that has come; one further out waits for the full view.
    const showsDeadline = todo.deadline !== undefined && !done && (!light || due);
    if (age !== undefined || showsLater || showsDeadline || habit) {
      side = (
        <span className={`side edge${habit ? " habit-side" : ""}`}>
          {age !== undefined && (
            <span className="age" title={t("In Today for {count} days", { count: age })}><Icon name="hourglass" />{t("{count}d", { count: age })}</span>
          )}
          {showsLater && (
            <span className="later-start" aria-label={t("Scheduled for {day}", { day: later })}><Icon name="calendar" />{startLabel(later, today)}</span>
          )}
          {showsDeadline && (
            <span className={`deadline${due ? " red" : ""}`} aria-label={deadlineSpoken(todo.deadline!, today)}>
              <Icon name="due" />{deadlineLabel(todo.deadline!, today)}
            </span>
          )}
          {habit && <HabitBadgeView badge={habit.badge} progress={habit.progress} />}
        </span>
      );
    }
  }

  const placeTitle = place?.kind === "area" ? splitEmoji(place.title) : null;

  return (
    <>
    <div ref={element}
      className={`row${todo.habitID !== undefined ? " habit" : ""}${due ? " overdue" : ""}${done ? " done" : ""}${lingering ? " lingering" : ""}${quietTick ? " quiet-tick" : ""}${selected ? " selected" : ""}${later && route.kind !== "scheduled" ? " later" : ""}${someday ? " someday" : ""}${someday && !staleList ? " faded" : ""}${!done && todo.start.kind === "someday" ? " muted-check" : ""}`}
      role="option" aria-selected={selected} tabIndex={selected || (ui.selected.length === 0 && window.visibleIDs[0] === todo.id) ? 0 : -1}
      data-row={todo.id} onClick={click} onDoubleClick={(event) => {
        if ((event.target as HTMLElement).closest("button") || layout.touch) return;
        openTodo(todo.id);
      }}
      onKeyDown={key} onContextMenu={rowMenu.onContextMenu} {...rowDragProps(window, todo)} {...(drop ?? rowDropProps(window, todo, inToday, onPlace))}>
      {swipe.action === "delete" ? (
        // A long swipe: Schedule and Delete side by side; once open, a tap on one does it.
        <span className={`swipe-actions${swipe.open ? " open" : ""}`} aria-hidden={!swipe.open}>
          <button className="swipe-schedule" tabIndex={-1} aria-label={habit ? t("Not Today") : t("Schedule")} disabled={!swipe.open}
            onClick={(event) => swipeButton(event, () => (habit ? restToday(model, habit.habit.id, window.showToast) : setSwiped("when")))}>
            <Icon name={habit ? "skip" : "calendar"} />
          </button>
          <button className="swipe-delete" tabIndex={-1} disabled={!swipe.open}
            onClick={(event) => swipeButton(event, () => {
              model.delete([todo.id]);
              window.showToast(t("To-do deleted"), true);
            })}>
            <Icon name="trash" /><span>{t("Delete")}</span>
          </button>
        </span>
      ) : swipe.action && (
        <span className={`swipe-tile ${swipe.action}${swipe.armed ? " armed" : ""}`} aria-hidden="true">
          <Icon name={swipe.action === "schedule" ? (habit ? "skip" : "calendar") : "move"} />
        </span>
      )}
      <button className="check tickbox" aria-label={done ? t("Mark as Not Done") : t("Complete")} aria-pressed={done}
        onClick={(event) => { event.stopPropagation(); onComplete([todo.id]); }}>
        {/* Behind the fill: the ring of the moment of ticking (app.css, "The moment of ticking"). */}
        <span className="tick-ring" aria-hidden="true" />
        <span className="fill" aria-hidden="true" />
        <Icon name="check" className="hint" />
        <svg className="tick" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path pathLength={1} d="M5.6 10.4 8.7 13.4 14.4 7.2" /></svg>
      </button>
      <div className="row-text">
        <div className="title-line">
          <span className="title">{todo.title || <span className="untitled">{t("New To-Do")}</span>}</span>
          {someday && <Icon name="leaf" className="someday-leaf" label={t("Someday")} />}
          {marks.length > 0 && <span className="marks">{marks.map((mark) => <Icon key={mark} name={mark} />)}</span>}
        </div>
        {(place || habitDetail || often) && (
          <div className={`where${often || habitDetail ? " with-habit" : ""}`}>
            {place?.project && place.kind === "project" && <Ring progress={lists.progress(place.project)} size={11} className="place-ring" color="var(--quiet-glyph)" />}
            {place?.kind === "list" && <Icon name="list" className="place-glyph" />}
            {place?.kind === "birthdays" && <Icon name="gift" className="place-glyph" />}
            {/* An area's emoji as a quiet glyph, small and grey, as a project's ring (Bastian, 2026-11-04). */}
            {placeTitle?.emoji && <span className="place-emoji" aria-hidden="true">{placeTitle.emoji}</span>}
            {place && <span className="where-name">{placeTitle ? placeTitle.name : place.title}</span>}
            {(often || habitDetail) && (
              // How often and the status shrink first, with an ellipsis, so the place keeps its name on a narrow row.
              <span className="habit-meta">
                {often && <span className="habit-often">{place ? "· " : ""}{often}</span>}
                {habitDetail && (
                  <span className={`habit-detail${habitState?.kind === "missed" ? " missed" : ""}`}>{place || often ? " · " : ""}{habitDetail}</span>
                )}
              </span>
            )}
          </div>
        )}
      </div>
      {side ?? <span />}
    </div>
    {rowMenu.menu}
    {swiped && (
      <Popover anchor={element} onClose={() => setSwiped(null)} label={swiped === "when" ? t("When") : t("Move")}>
        {swiped === "when" ? (
          <WhenPicker start={todo.start} today={today} onChoose={(start) => { model.schedule([todo.id], start); setSwiped(null); }} />
        ) : (
          <MovePicker areas={state.records.areas} projects={state.records.projects} current={destinationOf(todo)} progress={(project) => lists.progress(project)}
            onChoose={(destination) => { model.move([todo.id], destination); setSwiped(null); window.showToast(t("Moved"), true); }}
            onClose={() => setSwiped(null)} />
        )}
      </Popover>
    )}
    </>
  );
}
